import {
  AccountCircle as AccountIcon,
  Checkroom as HangerIcon,
  Clear as ClearIcon,
  ContentCopy as ContentCopyIcon,
  Devices as DevicesIcon,
  InfoOutlined as InfoIcon,
  Inventory as InventoryIcon,
  Launch as LaunchIcon,
  OpenInNew as OpenInNewIcon,
  VideogameAsset as SimulatorIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  GridLegacy as Grid,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useSearchParams } from "react-router-dom";

import { AdminDrawer, AdminFilterPanel, PageShell } from "@/components/common";
import { LoadingRing } from "@/components/common/LoadingRing";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { useAuth } from "@/contexts/AuthContext";
import { getApiConfig } from "@/config/api.config";
import { InventoryService } from "@/services";
import { CoreService } from "@/services/core/CoreService";
import { DebugService } from "@/services/features/DebugService";
import { LiveViewService } from "@/services/features/LiveViewService";
import {
  ChangingRoom,
  ChangingRoomGroup,
  Store,
  StoreService,
} from "@/services/features/StoreService";
import { StateRequest } from "@/types/state";
import { formatCurrencyInStore } from "@/utils/currency";
import { formatStoreSelectorLabel } from "@/utils/store";
import { formatStoreTimeWithTimezone, getBrowserTimezone } from "@/utils/storeTime";

interface StoreOption {
  id: number;
  label: string;
}

interface ChangingRoomGroupOption {
  id: number;
  label: string;
  storeId: number;
}

interface ChangingRoomOption {
  id: number;
  label: string;
  storeId: number;
}

interface Shopper {
  id: number;
  name: string | null;
  items: string[];
  roomId: number;
  storeId: number;
}

interface EnrichedItem {
  id?: string;
  sku: string;
  title?: string;
  imageUrl?: string;
  price?: string;
  salePrice?: string;
  color?: string;
  size?: string;
  brand?: string;
  quantity?: number;
  inStoreQuantity?: number;
}

interface EnrichedRequest {
  id: number;
  type: string;
  status: StateRequest["status"];
  createdAt: string;
  assignedAt?: string;
  completedAt?: string;
  changingRoomId: number;
  createdBy: number;
  assignedUserId?: number;
  sku?: string;
  enrichedItem?: {
    title?: string;
    imageUrl?: string;
    price?: string;
    salePrice?: string;
    color?: string;
    size?: string;
    brand?: string;
  };
}

type ShopperNameMode = "custom" | "none" | "random";
const NON_CANCELABLE_REQUEST_STATUSES = new Set<StateRequest["status"]>([
  "COMPLETED",
  "MISSED",
  "REQUEST_DELETED",
  "ITEM_NOT_FOUND_ALTERNATIVE",
  "ITEM_NOT_FOUND",
  "ITEM_NOT_FOUND_NO_ALTERNATIVE",
  "CANCELED",
]);

const isRequestCancelable = (status: StateRequest["status"]) =>
  !NON_CANCELABLE_REQUEST_STATUSES.has(status);

const RANDOM_SHOPPER_NAMES = [
  "Mr. Fred",
  "Señor Fred",
  "Monsieur Fred",
  "Signore Fred",
  "Mobile Boi Fred",
  "Fredward",
  "Sleep Noodle Fred",
  "TerrBear",
  "Terrence Berrence",
  "Iceboat Terr",
  "Fabiani Model Terry",
  "Mobile Boi Terrence",
  "Terry Berry",
  "Terr Durr",
  "Scrum Daddy Dano",
  "Daniel O' Sullivan",
  "Coach Dano",
  "melissium",
  "Allegedly Melissa",
  "Trustworthy Prince Amir",
  "OG Amir",
  "Feli the Bug Catcher",
  "Hi Felicia!",
  "Meli Bestie",
];

const Simulator: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const browserTimezone = getBrowserTimezone();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchParams, setSearchParams] = useSearchParams();

  const [stores, setStores] = useState<Store[]>([]);
  const [changingRoomGroups, setChangingRoomGroups] = useState<ChangingRoomGroup[]>([]);
  const [changingRooms, setChangingRooms] = useState<ChangingRoom[]>([]);
  const [selectedStore, setSelectedStore] = useState<StoreOption | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<ChangingRoomGroupOption | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<ChangingRoomOption | null>(null);
  const [shopper, setShopper] = useState<Shopper | null>(null);
  const [createShopperName, setCreateShopperName] = useState("");
  const [overrideShopperName, setOverrideShopperName] = useState("");
  const [shopperNameMode, setShopperNameMode] = useState<ShopperNameMode>("random");
  const [currentSku, setCurrentSku] = useState("");
  const [requestSku, setRequestSku] = useState("");
  const [inventoryDrawerOpen, setInventoryDrawerOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Add new state for items
  const [items, setItems] = useState<EnrichedItem[]>([]);

  // Add new state for requests
  const [requests, setRequests] = useState<EnrichedRequest[]>([]);

  // Add state for sample inventory items
  const [sampleInventoryItems, setSampleInventoryItems] = useState<EnrichedItem[]>([]);
  // Keep all inventory items for random selection
  const [allInventoryItems, setAllInventoryItems] = useState<EnrichedItem[]>([]);
  const selectedStoreRecord = selectedStore
    ? (stores.find((store) => store.id === selectedStore.id) ?? null)
    : null;
  const roomDevicesUrl =
    selectedStoreRecord && selectedRoom
      ? `/devices?${new URLSearchParams({
          customerId: selectedStoreRecord.customerId.toString(),
          storeId: selectedStoreRecord.id.toString(),
          roomId: selectedRoom.id.toString(),
        }).toString()}`
      : null;
  const effectiveTimezone = selectedStoreRecord?.timezone || browserTimezone;
  const [loadingSampleInventory, setLoadingSampleInventory] = useState(false);

  // Add component-specific loading states
  const [loadingStores, setLoadingStores] = useState(false);
  const [loadingRoomGroups, setLoadingRoomGroups] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const showFeedback = useCallback(
    (message: string, variant: "success" | "error") => {
      enqueueSnackbar(message, { variant });
    },
    [enqueueSnackbar],
  );
  const storeService = StoreService.getInstance();
  const debugService = DebugService.getInstance();
  const coreService = useMemo(() => CoreService.getInstance(), []);
  const inventoryService = useMemo(
    () => new InventoryService(coreService.getAuthorizationHeader() || ""),
    [coreService],
  );
  const liveViewService = useMemo(() => LiveViewService.getInstance(), []);

  const inventoryBaseUrl = getApiConfig().inventory;

  // Generate inventory URL
  const inventoryUrl = useMemo(() => {
    if (!selectedStore) return null;
    return `${inventoryBaseUrl}/sample/${selectedStore.id}/items`;
  }, [inventoryBaseUrl, selectedStore]);

  useEffect(() => {
    document.title = `${t("layout.sidebar.craveAdminSection.fields.simulator")} - Crave Admin`;
    fetchInitialData();
  }, [t]);

  useEffect(() => {
    if (selectedStore) {
      fetchStoreData(selectedStore.id);
    }
  }, [selectedStore]);

  useEffect(() => {
    if (selectedGroup) {
      fetchRoomData();
    }
  }, [selectedGroup]);

  // Add a new effect to fetch room state when a room is selected
  useEffect(() => {
    if (selectedStore && selectedRoom) {
      fetchRoomState(selectedRoom.id);
    }
  }, [selectedRoom]);

  useEffect(() => {
    if (!selectedRoom) {
      setInventoryDrawerOpen(false);
    }
  }, [selectedRoom]);

  const fetchInitialData = async () => {
    setLoadingStores(true);
    try {
      const response = await storeService.getStores();
      if (response.data) {
        setStores(response.data);
      } else {
        showFeedback(t("operations.debug.notifications.failedFetchStores"), "error");
      }
    } catch {
      showFeedback(t("operations.debug.notifications.failedFetchInitialData"), "error");
    }
    setLoadingStores(false);
  };

  const fetchStoreData = async (storeId: number) => {
    setLoadingRoomGroups(true);

    // Fetch changing room groups
    try {
      const groupsResponse = await storeService.getChangingRoomsByStore(storeId);

      if (groupsResponse.data) {
        const validGroups = groupsResponse.data.filter(
          (group) => group && group.changingRoomGroup && group.changingRoomGroup.id,
        );
        setChangingRoomGroups(validGroups);
      } else {
      }
    } catch {
      showFeedback(t("operations.debug.notifications.failedFetchChangingRoomGroups"), "error");
    } finally {
      setLoadingRoomGroups(false);
    }

    // Fetch sample inventory items
    fetchSampleInventory(storeId);
  };

  const fetchSampleInventory = async (storeId: number) => {
    setLoadingSampleInventory(true);
    setSampleInventoryItems([]);
    setAllInventoryItems([]);

    try {
      const response = await inventoryService.fetchInventoryPage(storeId, 1);

      // Store all items for random selection
      const allItems: EnrichedItem[] = response.map((item) => ({
        id: item.id,
        sku: item.skuId,
        title: item.title,
        imageUrl: item.imageUrl,
        price: item.price,
        salePrice: item.salePrice || undefined,
        color: item.color,
        size: item.size,
        brand: item.brand,
        quantity: item.quantity,
        inStoreQuantity: item.inStoreQuantity,
      }));
      setAllInventoryItems(allItems);

      // Extract up to 10 items from different products for display
      const seenProductIds = new Set<string>();
      const sampleItems: EnrichedItem[] = [];

      for (const item of response) {
        if (sampleItems.length >= 10) break;
        if (!seenProductIds.has(item.productId)) {
          seenProductIds.add(item.productId);
          sampleItems.push({
            id: item.id,
            sku: item.skuId,
            title: item.title,
            imageUrl: item.imageUrl,
            price: item.price,
            salePrice: item.salePrice || undefined,
            color: item.color,
            size: item.size,
            brand: item.brand,
            quantity: item.quantity,
            inStoreQuantity: item.inStoreQuantity,
          });
        }
      }

      setSampleInventoryItems(sampleItems);
    } catch (error) {
      console.error("Failed to fetch sample inventory:", error);
    } finally {
      setLoadingSampleInventory(false);
    }
  };

  const fetchRoomData = async () => {
    if (!selectedGroup) return;

    setLoadingRooms(true);
    try {
      const selectedGroupData = changingRoomGroups.find(
        (group) => group.changingRoomGroup.id === selectedGroup.id,
      );
      if (selectedGroupData) {
        setChangingRooms(selectedGroupData.changingRooms || []);
      } else {
        setChangingRooms([]);
      }
    } catch {
      enqueueSnackbar(t("operations.debug.notifications.failedFetchRoomData"), {
        variant: "error",
      });
    }
    setLoadingRooms(false);
  };

  const handleCreateShopper = async (nextShopperName = getCreateShopperName()) => {
    if (!selectedStore || !selectedRoom) {
      showFeedback(t("operations.debug.messages.selectStoreAndRoomBeforeShopper"), "error");
      return;
    }

    // Create a shopper without any initial items
    handleCreateShopperWithSkus([], nextShopperName);
  };

  const handleCreateShopperWithRandomItems = async (
    count = 15,
    nextShopperName = getCreateShopperName(),
  ) => {
    if (!selectedStore || !selectedRoom) {
      showFeedback(t("operations.debug.messages.selectStoreAndRoomBeforeShopper"), "error");
      return;
    }

    if (allInventoryItems.length === 0) {
      showFeedback(t("operations.debug.messages.noInventoryItemsAvailable"), "error");
      return;
    }

    // Select a bounded number of random items from all inventory items
    const randomSkus: string[] = [];
    const availableItems = [...allInventoryItems];
    const itemCount = Math.min(count, availableItems.length);

    for (let i = 0; i < itemCount; i++) {
      const randomIndex = Math.floor(Math.random() * availableItems.length);
      randomSkus.push(availableItems[randomIndex].sku);
      // Remove the selected item to avoid duplicates
      availableItems.splice(randomIndex, 1);
    }

    // Create shopper with the random SKUs
    handleCreateShopperWithSkus(randomSkus, nextShopperName);
  };

  const getCreateShopperName = (): string | null => {
    if (shopperNameMode === "none") {
      return null;
    }

    if (shopperNameMode === "random") {
      const randomIndex = Math.floor(Math.random() * RANDOM_SHOPPER_NAMES.length);
      return RANDOM_SHOPPER_NAMES[randomIndex];
    }

    return createShopperName.trim() || null;
  };

  // New function to create shopper with selected SKUs
  const handleCreateShopperWithSkus = async (skuList: string[], nextShopperName: string | null) => {
    if (!selectedStore || !selectedRoom) {
      showFeedback(t("operations.debug.messages.selectStoreAndRoomBeforeShopper"), "error");
      return;
    }

    setActionLoading(true);
    try {
      const response = await debugService.createDebugShopper({
        name: nextShopperName,
        items: skuList,
        roomId: selectedRoom.id,
        storeId: selectedStore.id,
      });

      if (response.success && response.data) {
        setShopper(response.data);
        setOverrideShopperName(response.data.name || nextShopperName || "");
        // Initialize items - enrich them if there are any
        if (skuList.length > 0 && selectedStore) {
          await enrichItems(skuList, selectedStore.id);
        } else {
          setItems([]);
        }
        showFeedback(
          nextShopperName
            ? t("operations.debug.messages.shopperCreatedNamed", {
                shopperName: nextShopperName,
              })
            : t("operations.debug.messages.shopperCreatedNameless"),
          "success",
        );
      } else {
        throw new Error(response.error || t("operations.debug.notifications.failedCreateShopper"));
      }
    } catch {
      showFeedback(t("operations.debug.messages.errorCreatingShopper"), "error");
    }
    setActionLoading(false);
  };

  const enrichItems = useCallback(
    async (skuIds: string[], storeId: number) => {
      console.log("Enriching items:", skuIds, "for store:", storeId);
      const enrichedItems: EnrichedItem[] = await Promise.all(
        skuIds.map(async (skuId) => {
          try {
            const response = await inventoryService.fetchProductDetails(storeId, skuId);
            console.log("Product details response for", skuId, ":", response);
            const skuDetails = response?.data?.skus?.find((sku: any) => sku.skuId === skuId);

            if (skuDetails) {
              const enrichedItem = {
                sku: skuId,
                title: skuDetails.title,
                imageUrl: skuDetails.imageUrl,
                price: skuDetails.price,
                salePrice: skuDetails.salePrice || undefined,
                color: skuDetails.color,
                size: skuDetails.size,
                brand: skuDetails.brand,
              };
              console.log("Enriched item:", enrichedItem);
              return enrichedItem;
            }
          } catch (error) {
            console.error("Error enriching item", skuId, ":", error);
          }
          console.log("Returning basic item for:", skuId);
          return { sku: skuId };
        }),
      );
      console.log("Final enriched items:", enrichedItems);
      setItems(enrichedItems);
    },
    [inventoryService],
  );

  const enrichRequests = useCallback(
    async (roomRequests: StateRequest[], storeId: number): Promise<EnrichedRequest[]> => {
      return Promise.all(
        roomRequests.map(async (request: StateRequest) => {
          if (request.type === "ITEM" && request.sku) {
            try {
              const response = await inventoryService.fetchProductDetails(storeId, request.sku);
              const skuDetails = response?.data?.skus?.find(
                (sku: any) => sku.skuId === request.sku,
              );

              if (skuDetails) {
                return {
                  ...request,
                  enrichedItem: {
                    title: skuDetails.title,
                    imageUrl: skuDetails.imageUrl,
                    price: skuDetails.price,
                    salePrice: skuDetails.salePrice || undefined,
                    color: skuDetails.color,
                    size: skuDetails.size,
                    brand: skuDetails.brand,
                  },
                };
              }
            } catch {
              // Ignore enrichment errors to keep debug flow resilient
            }
          }
          return request;
        }),
      );
    },
    [inventoryService],
  );

  const handleCreateRequest = useCallback(async () => {
    if (!shopper || !selectedRoom || !selectedStore) {
      showFeedback(t("operations.debug.messages.selectShopperRoomStoreBeforeRequest"), "error");
      return;
    }

    if (!requestSku) {
      showFeedback(t("operations.debug.messages.enterSkuForRequest"), "error");
      return;
    }

    setActionLoading(true);
    try {
      const response = await debugService.createDebugRequest({
        status: "CREATED",
        createdBy: shopper.id,
        changingRoomId: selectedRoom.id,
        requestType: "ITEM",
        sku: requestSku,
      });

      if (response.success) {
        showFeedback(t("operations.debug.messages.requestCreated"), "success");
        setRequestSku("");

        // Fetch updated state to get request details
        const stateResponse = await liveViewService.getState({
          roomIds: [selectedRoom.id],
          includeStandaloneShoppers: true,
          includeOnHoldShoppers: false,
        });

        if (stateResponse.data) {
          const roomRequests = liveViewService.getRequestsByRoom(stateResponse, selectedRoom.id);
          const enrichedRequests = await enrichRequests(roomRequests, selectedStore.id);
          setRequests(enrichedRequests);
        }
      } else {
        throw new Error(response.error || t("operations.debug.notifications.failedCreateRequest"));
      }
    } catch {
      showFeedback(t("operations.debug.messages.errorCreatingRequest"), "error");
    }
    setActionLoading(false);
  }, [
    debugService,
    enrichRequests,
    liveViewService,
    requestSku,
    selectedRoom,
    selectedStore,
    showFeedback,
    shopper,
    t,
  ]);

  const handleCreateRandomRequest = useCallback(async () => {
    if (!shopper || !selectedRoom || !selectedStore) {
      showFeedback(t("operations.debug.messages.selectShopperRoomStoreBeforeRequest"), "error");
      return;
    }

    if (allInventoryItems.length === 0) {
      showFeedback(t("operations.debug.messages.noInventoryItemsAvailable"), "error");
      return;
    }

    // Select a random item from all inventory items
    const randomIndex = Math.floor(Math.random() * allInventoryItems.length);
    const randomSku = allInventoryItems[randomIndex].sku;

    setActionLoading(true);
    try {
      const response = await debugService.createDebugRequest({
        status: "CREATED",
        createdBy: shopper.id,
        changingRoomId: selectedRoom.id,
        requestType: "ITEM",
        sku: randomSku,
      });

      if (response.success) {
        showFeedback(t("operations.debug.messages.randomRequestCreated"), "success");

        // Fetch updated state to get request details
        const stateResponse = await liveViewService.getState({
          roomIds: [selectedRoom.id],
          includeStandaloneShoppers: true,
          includeOnHoldShoppers: false,
        });

        if (stateResponse.data) {
          const roomRequests = liveViewService.getRequestsByRoom(stateResponse, selectedRoom.id);
          const enrichedRequests = await enrichRequests(roomRequests, selectedStore.id);
          setRequests(enrichedRequests);
        }
      } else {
        throw new Error(
          response.error || t("operations.debug.notifications.failedCreateRandomRequest"),
        );
      }
    } catch {
      showFeedback(t("operations.debug.messages.errorCreatingRandomRequest"), "error");
    }
    setActionLoading(false);
  }, [
    allInventoryItems,
    debugService,
    enrichRequests,
    liveViewService,
    selectedRoom,
    selectedStore,
    showFeedback,
    shopper,
    t,
  ]);

  const handleCreateAssociateRequest = useCallback(async () => {
    if (!selectedRoom || !selectedStore) {
      showFeedback(t("operations.debug.messages.selectShopperRoomStoreBeforeRequest"), "error");
      return;
    }

    setActionLoading(true);
    try {
      const response = await debugService.createDebugRequest({
        status: "CREATED",
        ...(shopper ? { createdBy: shopper.id } : {}),
        changingRoomId: selectedRoom.id,
        requestType: "ASSOCIATE",
      });

      if (response.success) {
        showFeedback(t("operations.debug.messages.helpRequestCreated"), "success");

        const stateResponse = await liveViewService.getState({
          roomIds: [selectedRoom.id],
          includeStandaloneShoppers: true,
          includeOnHoldShoppers: false,
        });

        if (stateResponse.data) {
          const roomRequests = liveViewService.getRequestsByRoom(stateResponse, selectedRoom.id);
          const enrichedRequests = await enrichRequests(roomRequests, selectedStore.id);
          setRequests(enrichedRequests);
        }
      } else {
        throw new Error(
          response.error || t("operations.debug.notifications.failedCreateHelpRequest"),
        );
      }
    } catch {
      showFeedback(t("operations.debug.messages.errorCreatingHelpRequest"), "error");
    }
    setActionLoading(false);
  }, [
    debugService,
    enrichRequests,
    liveViewService,
    selectedRoom,
    selectedStore,
    showFeedback,
    shopper,
    t,
  ]);

  const handleCancelAllRequests = useCallback(async () => {
    if (!selectedRoom || !selectedStore) {
      showFeedback(t("operations.debug.messages.selectShopperRoomStoreBeforeRequest"), "error");
      return;
    }

    const requestsToCancel = requests.filter((request) => isRequestCancelable(request.status));

    if (requestsToCancel.length === 0) {
      showFeedback(t("operations.debug.messages.noRequestsToCancel"), "error");
      return;
    }

    setActionLoading(true);
    try {
      for (const request of requestsToCancel) {
        const response = await debugService.updateDebugRequestStatus({
          id: request.id,
          status: "CANCELED",
          assignedUserId: request.assignedUserId ?? null,
        });

        if (!response.success) {
          throw new Error(
            response.error || t("operations.debug.notifications.failedCancelRequests"),
          );
        }
      }

      const stateResponse = await liveViewService.getState({
        roomIds: [selectedRoom.id],
        includeStandaloneShoppers: true,
        includeOnHoldShoppers: false,
      });

      if (stateResponse.data) {
        const roomRequests = liveViewService.getRequestsByRoom(stateResponse, selectedRoom.id);
        const enrichedRequests = await enrichRequests(roomRequests, selectedStore.id);
        setRequests(enrichedRequests);
      }

      showFeedback(t("operations.debug.messages.allRequestsCanceled"), "success");
    } catch {
      showFeedback(t("operations.debug.messages.errorCancelingRequests"), "error");
    } finally {
      setActionLoading(false);
    }
  }, [
    debugService,
    enrichRequests,
    liveViewService,
    requests,
    selectedRoom,
    selectedStore,
    showFeedback,
    t,
  ]);

  const handleVacateShopper = useCallback(async () => {
    if (!shopper) {
      showFeedback(t("operations.debug.messages.noShopperToVacate"), "error");
      return;
    }

    setActionLoading(true);
    try {
      const response = await debugService.vacateDebugShopper(shopper.id);
      if (response.success) {
        setShopper(null);
        setOverrideShopperName("");
        setItems([]);
        setRequests([]);
        showFeedback(t("operations.debug.messages.shopperVacated"), "success");
      } else {
        throw new Error(response.error || t("operations.debug.notifications.failedVacateShopper"));
      }
    } catch {
      showFeedback(t("operations.debug.messages.errorVacatingShopper"), "error");
    }
    setActionLoading(false);
  }, [debugService, shopper, showFeedback, t]);

  const handleAbandonShopper = useCallback(async () => {
    if (!selectedRoom) {
      showFeedback(t("operations.debug.messages.noRoomSelected"), "error");
      return;
    }

    setActionLoading(true);
    try {
      const response = await debugService.abandonDebugShopper(selectedRoom.id);
      if (response.success) {
        setShopper(null);
        setOverrideShopperName("");
        setItems([]);
        setRequests([]);
        showFeedback(t("operations.debug.messages.shopperAbandoned"), "success");
      } else {
        throw new Error(response.error || t("operations.debug.notifications.failedAbandonShopper"));
      }
    } catch {
      showFeedback(t("operations.debug.messages.errorAbandoningShopper"), "error");
    }
    setActionLoading(false);
  }, [debugService, selectedRoom, setActionLoading, showFeedback, t]);

  const handleAddSku = useCallback(async () => {
    if (!shopper) {
      showFeedback(t("operations.debug.messages.noShopperToAddItem"), "error");
      return;
    }

    if (!currentSku) {
      showFeedback(t("operations.debug.messages.enterSkuToAdd"), "error");
      return;
    }

    setActionLoading(true);
    try {
      const response = await debugService.addDebugItem(shopper.id, [currentSku]);
      if (response.success) {
        showFeedback(t("operations.debug.messages.itemAdded"), "success");
        // Re-enrich all items including the new one
        if (selectedStore) {
          const newItems = [...items.map((i) => i.sku), currentSku];
          await enrichItems(newItems, selectedStore.id);
        }
        setCurrentSku("");
      } else {
        throw new Error(response.error || t("operations.debug.notifications.failedAddItem"));
      }
    } catch {
      showFeedback(t("operations.debug.messages.errorAddingItem"), "error");
    }
    setActionLoading(false);
  }, [currentSku, debugService, enrichItems, items, selectedStore, showFeedback, shopper, t]);

  const handleAddRandomItem = useCallback(async () => {
    if (!shopper) {
      showFeedback(t("operations.debug.messages.noShopperToAddItem"), "error");
      return;
    }

    if (allInventoryItems.length === 0) {
      showFeedback(t("operations.debug.messages.noInventoryItemsAvailable"), "error");
      return;
    }

    // Select a random item from all inventory items
    const randomIndex = Math.floor(Math.random() * allInventoryItems.length);
    const randomSku = allInventoryItems[randomIndex].sku;

    setActionLoading(true);
    try {
      const response = await debugService.addDebugItem(shopper.id, [randomSku]);
      if (response.success) {
        showFeedback(t("operations.debug.messages.randomItemAdded"), "success");
        // Re-enrich all items including the new one
        if (selectedStore) {
          const newItems = [...items.map((i) => i.sku), randomSku];
          await enrichItems(newItems, selectedStore.id);
        }
      } else {
        throw new Error(response.error || t("operations.debug.notifications.failedAddRandomItem"));
      }
    } catch {
      showFeedback(t("operations.debug.messages.errorAddingRandomItem"), "error");
    }
    setActionLoading(false);
  }, [
    allInventoryItems,
    debugService,
    enrichItems,
    items,
    selectedStore,
    showFeedback,
    shopper,
    t,
  ]);

  const handleAddRandomOutOfStockItem = useCallback(async () => {
    if (!shopper) {
      showFeedback(t("operations.debug.messages.noShopperToAddItem"), "error");
      return;
    }

    const outOfStockItems = allInventoryItems.filter((item) => {
      const storeQuantity = item.inStoreQuantity ?? item.quantity;
      return storeQuantity === 0;
    });

    if (outOfStockItems.length === 0) {
      showFeedback(t("operations.debug.messages.noOutOfStockInventoryItemsAvailable"), "error");
      return;
    }

    const randomIndex = Math.floor(Math.random() * outOfStockItems.length);
    const randomSku = outOfStockItems[randomIndex].sku;

    setActionLoading(true);
    try {
      const response = await debugService.addDebugItem(shopper.id, [randomSku]);
      if (response.success) {
        showFeedback(t("operations.debug.messages.randomOutOfStockItemAdded"), "success");
        if (selectedStore) {
          const newItems = [...items.map((i) => i.sku), randomSku];
          await enrichItems(newItems, selectedStore.id);
        }
      } else {
        throw new Error(response.error || t("operations.debug.notifications.failedAddRandomItem"));
      }
    } catch {
      showFeedback(t("operations.debug.messages.errorAddingRandomItem"), "error");
    }
    setActionLoading(false);
  }, [
    allInventoryItems,
    debugService,
    enrichItems,
    items,
    selectedStore,
    showFeedback,
    shopper,
    t,
  ]);

  const handleClearAllItems = useCallback(async () => {
    if (!shopper) return;

    setActionLoading(true);
    try {
      const response = await debugService.clearDebugItems(shopper);
      if (response.success) {
        setItems([]);
        showFeedback(t("operations.debug.messages.allItemsCleared"), "success");
      } else {
        throw new Error(response.error || t("operations.debug.notifications.failedClearItems"));
      }
    } catch {
      showFeedback(t("operations.debug.messages.errorClearingItems"), "error");
    }
    setActionLoading(false);
  }, [debugService, shopper, showFeedback, t]);

  // Add a new function to fetch room state
  const fetchRoomState = async (roomId: number) => {
    if (!selectedStore) return;

    try {
      setActionLoading(true);
      const stateResponse = await liveViewService.getState({
        roomIds: [roomId],
        includeStandaloneShoppers: true,
        includeOnHoldShoppers: false,
      });

      if (stateResponse.data) {
        const shopperDetails = liveViewService.getShopperByRoom(stateResponse, roomId);

        if (shopperDetails) {
          const shopperItems = liveViewService.getItemsByRoom(stateResponse, roomId);

          setShopper({
            id: shopperDetails.id,
            name: shopperDetails.name || null,
            items: shopperItems.map((item) => item.sku),
            roomId: roomId,
            storeId: selectedStore.id,
          });
          setOverrideShopperName(shopperDetails.name || "");

          // Enrich the items
          const skuIds = shopperItems.map((item) => item.sku);
          await enrichItems(skuIds, selectedStore.id);

          const roomRequests = liveViewService.getRequestsByRoom(stateResponse, roomId);
          const enrichedRequests = await enrichRequests(roomRequests, selectedStore.id);
          setRequests(enrichedRequests);
        } else {
          // Room is vacant
          setShopper(null);
          setOverrideShopperName("");
          setItems([]);
          const roomRequests = liveViewService.getRequestsByRoom(stateResponse, roomId);
          const enrichedRequests = await enrichRequests(roomRequests, selectedStore.id);
          setRequests(enrichedRequests);
        }
        setHasLoadedRoomState(true);
      }
    } catch {
      enqueueSnackbar(t("operations.debug.notifications.failedFetchRoomState"), {
        variant: "error",
      });
      setHasLoadedRoomState(true);
    } finally {
      setActionLoading(false);
    }
  };

  const storeOptions: StoreOption[] = stores.map((store) => ({
    id: store.id,
    label: formatStoreSelectorLabel(store, t, user?.role, "id-name"),
  }));

  const groupOptions: ChangingRoomGroupOption[] = changingRoomGroups
    .filter((group) => group.changingRoomGroup?.id !== undefined)
    .map((group) => ({
      id: group.changingRoomGroup.id!,
      label: group.changingRoomGroup.name,
      storeId: group.changingRoomGroup.storeId,
    }));

  const roomOptions: ChangingRoomOption[] = changingRooms
    .filter((room) => room.id !== undefined)
    .map((room) => ({
      id: room.id!,
      label: room.name,
      storeId: selectedStore?.id || 0,
    }));

  // Track if we've loaded from URL params
  const [hasLoadedFromUrl, setHasLoadedFromUrl] = useState(false);

  // Track if room state has been loaded
  const [hasLoadedRoomState, setHasLoadedRoomState] = useState(false);

  // Add a function to update URL parameters when selections change
  const updateUrlParams = useCallback(() => {
    // Don't update URL params until we've loaded from URL first
    if (!hasLoadedFromUrl) return;

    const params = new URLSearchParams();

    if (selectedStore) {
      params.set("storeId", selectedStore.id.toString());
    }

    if (selectedGroup) {
      params.set("groupId", selectedGroup.id.toString());
    }

    if (selectedRoom) {
      params.set("roomId", selectedRoom.id.toString());
    }

    setSearchParams(params, { replace: true });
  }, [selectedStore, selectedGroup, selectedRoom, setSearchParams, hasLoadedFromUrl]);

  // Effect to handle URL parameter initialization on mount
  useEffect(() => {
    const loadFromUrlParams = async () => {
      const storeIdParam = searchParams.get("storeId");
      const groupIdParam = searchParams.get("groupId");
      const roomIdParam = searchParams.get("roomId");

      console.log("Loading from URL params:", {
        storeIdParam,
        groupIdParam,
        roomIdParam,
      });

      // If no parameters, mark as loaded and exit
      if (!storeIdParam && !groupIdParam && !roomIdParam) {
        setHasLoadedFromUrl(true);
        return;
      }

      try {
        let storeToUse: Store | undefined;

        // First, handle store selection if needed
        if (storeIdParam) {
          const storeId = parseInt(storeIdParam);
          if (isNaN(storeId)) {
            setHasLoadedFromUrl(true);
            return;
          }

          // Wait for stores to be loaded if not already
          let storesToSearch = stores;
          if (stores.length === 0) {
            const response = await storeService.getStores();
            if (response.data) {
              setStores(response.data);
              storesToSearch = response.data;
            }
          }

          storeToUse = storesToSearch.find((store) => store.id === storeId);

          if (storeToUse) {
            setSelectedStore({
              id: storeToUse.id,
              label: `${storeToUse.id} - ${storeToUse.name}`,
            });

            // Load store data which will fetch groups
            const groupsResponse = await storeService.getChangingRoomsByStore(storeId);
            if (groupsResponse.data) {
              const validGroups = groupsResponse.data.filter(
                (group) => group && group.changingRoomGroup && group.changingRoomGroup.id,
              );
              setChangingRoomGroups(validGroups);

              // Handle group selection if needed
              if (groupIdParam && validGroups.length > 0) {
                const groupId = parseInt(groupIdParam);
                if (!isNaN(groupId)) {
                  const groupToSelect = validGroups.find(
                    (group) => group.changingRoomGroup.id === groupId,
                  );

                  if (groupToSelect) {
                    setSelectedGroup({
                      id: groupToSelect.changingRoomGroup.id!,
                      label: groupToSelect.changingRoomGroup.name,
                      storeId: groupToSelect.changingRoomGroup.storeId,
                    });

                    const rooms = groupToSelect.changingRooms || [];
                    setChangingRooms(rooms);

                    // Handle room selection if needed
                    if (roomIdParam && rooms.length > 0) {
                      const roomId = parseInt(roomIdParam);
                      if (!isNaN(roomId)) {
                        const roomToSelect = rooms.find((room) => room.id === roomId);

                        if (roomToSelect) {
                          setSelectedRoom({
                            id: roomToSelect.id!,
                            label: roomToSelect.name,
                            storeId: storeId,
                          });

                          // Fetch room state to update UI
                          await fetchRoomState(roomId);
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error("Error loading from URL params:", error);
        enqueueSnackbar(t("operations.debug.notifications.failedInitializeFromUrl"), {
          variant: "error",
        });
      } finally {
        setHasLoadedFromUrl(true);
      }
    };

    // Only run once on mount
    if (!hasLoadedFromUrl) {
      loadFromUrlParams();
    }
  }, []);

  // Add effect to update URL when selections change
  useEffect(() => {
    updateUrlParams();
  }, [selectedStore, selectedGroup, selectedRoom, updateUrlParams]);

  // Update the selection handlers to include URL updates
  const handleStoreChange = (_event: React.SyntheticEvent | null, newValue: StoreOption | null) => {
    setSelectedStore(newValue);
    setSelectedGroup(null);
    setSelectedRoom(null);
    setChangingRoomGroups([]);
    setChangingRooms([]);
    setHasLoadedRoomState(false);
  };

  const handleGroupChange = (
    _event: React.SyntheticEvent | null,
    newValue: ChangingRoomGroupOption | null,
  ) => {
    setSelectedGroup(newValue);
    setSelectedRoom(null);
    setChangingRooms([]);
    setHasLoadedRoomState(false);
  };

  const handleRoomChange = (
    _event: React.SyntheticEvent | null,
    newValue: ChangingRoomOption | null,
  ) => {
    setSelectedRoom(newValue);
    setHasLoadedRoomState(false);
  };

  const getOverrideShopperName = () => overrideShopperName.trim() || null;

  const shopperNameModeHelpKey: Record<ShopperNameMode, string> = {
    custom: "operations.debug.nameModeHelp.custom",
    none: "operations.debug.nameModeHelp.none",
    random: "operations.debug.nameModeHelp.random",
  };

  const shopperControlSx = {
    p: 2,
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
    bgcolor: "grey.50",
    height: "100%",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
  };

  const renderCreateShopperControls = () => (
    <Box sx={shopperControlSx}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 0 }}>
        {t("operations.debug.sections.createShopper")}
      </Typography>
      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} lg={6}>
          <Stack spacing={1.5}>
            <ToggleButtonGroup
              color="primary"
              size="small"
              exclusive
              value={shopperNameMode}
              onChange={(_event, nextMode: ShopperNameMode | null) => {
                if (!nextMode) return;
                setShopperNameMode(nextMode);
              }}
              aria-label={t("operations.debug.accessibility.shopperNameMode")}
              sx={{
                display: "inline-flex",
                width: "100%",
                "& .MuiToggleButtonGroup-grouped": {
                  flex: "1 1 0",
                  px: 1.5,
                },
              }}
            >
              <ToggleButton value="random">{t("operations.debug.nameModes.random")}</ToggleButton>
              <ToggleButton value="custom">{t("operations.debug.nameModes.custom")}</ToggleButton>
              <ToggleButton value="none">{t("operations.debug.nameModes.none")}</ToggleButton>
            </ToggleButtonGroup>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                border: "1px solid",
                borderColor: "rgba(100, 116, 139, 0.24)",
                bgcolor: "rgba(100, 116, 139, 0.08)",
                borderRadius: 1,
                p: 1,
              }}
            >
              <InfoIcon sx={{ color: "#475569", fontSize: 18, mt: 0.125 }} />
              <Typography variant="caption" sx={{ color: "#475569", display: "block" }}>
                {t(shopperNameModeHelpKey[shopperNameMode])}
              </Typography>
            </Box>
            {shopperNameMode === "custom" && (
              <TextField
                fullWidth
                label={t("operations.debug.fields.shopperName")}
                value={createShopperName}
                onChange={(e) => setCreateShopperName(e.target.value)}
                placeholder={t("operations.debug.placeholders.shopperName")}
                size="small"
                margin="none"
              />
            )}
          </Stack>
        </Grid>
        <Grid
          item
          xs={12}
          md="auto"
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Divider
            orientation={isMobile ? "horizontal" : "vertical"}
            flexItem
            sx={{ width: { xs: "100%", md: "auto" } }}
          />
        </Grid>
        <Grid item xs={12} md>
          <Stack spacing={1}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                void handleCreateShopper();
              }}
              disabled={actionLoading}
              size="medium"
              sx={{ py: 0.75 }}
            >
              {t("operations.debug.actions.createShopper")}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                void handleCreateShopperWithRandomItems(1);
              }}
              disabled={actionLoading || loadingSampleInventory || allInventoryItems.length === 0}
              size="medium"
              sx={{ py: 0.75 }}
            >
              {t("operations.debug.actions.createShopperWithOneRandomItem")}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                void handleCreateShopperWithRandomItems(15);
              }}
              disabled={actionLoading || loadingSampleInventory || allInventoryItems.length === 0}
              size="medium"
              sx={{ py: 0.75 }}
            >
              {t("operations.debug.actions.createShopperWithRandomItems")}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );

  const renderOverrideShopperControls = () => (
    <Box sx={shopperControlSx}>
      <Box>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 1.5 }}>
          {t("operations.debug.actions.overrideShopper")}
        </Typography>
        <TextField
          fullWidth
          label={t("operations.debug.fields.shopperName")}
          value={overrideShopperName}
          onChange={(e) => setOverrideShopperName(e.target.value)}
          placeholder={t("operations.debug.placeholders.shopperName")}
          size="small"
          margin="none"
        />
      </Box>
      <Grid container spacing={1} sx={{ mt: "auto" }}>
        <Grid item xs={12}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              void handleCreateShopper(getOverrideShopperName());
            }}
            disabled={actionLoading}
            size="medium"
            sx={{ py: 0.75 }}
          >
            {t("operations.debug.actions.overrideShopper")}
          </Button>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              void handleCreateShopperWithRandomItems(1, getOverrideShopperName());
            }}
            disabled={actionLoading || loadingSampleInventory || allInventoryItems.length === 0}
            size="medium"
            sx={{ py: 0.75 }}
          >
            {t("operations.debug.actions.overrideShopperWithOneRandomItem")}
          </Button>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              void handleCreateShopperWithRandomItems(15, getOverrideShopperName());
            }}
            disabled={actionLoading || loadingSampleInventory || allInventoryItems.length === 0}
            size="medium"
            sx={{ py: 0.75 }}
          >
            {t("operations.debug.actions.overrideShopperWithRandomItems")}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );

  const exitControlSx = {
    p: 2,
    borderRadius: 1,
    bgcolor: "grey.50",
    border: "1px solid",
    borderColor: "divider",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: 2,
  };

  const renderExitShopperControl = () => (
    <Box sx={exitControlSx}>
      <Box>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 1 }}>
          {t("operations.debug.actions.vacateShopper")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            border: "1px solid",
            borderColor: "rgba(100, 116, 139, 0.24)",
            bgcolor: "rgba(100, 116, 139, 0.08)",
            borderRadius: 1,
            p: 1,
          }}
        >
          <InfoIcon sx={{ color: "#475569", fontSize: 18, mt: 0.125 }} />
          <Typography variant="caption" sx={{ color: "#475569", display: "block" }}>
            {t("operations.debug.help.vacateOrAbandonShopper")}
          </Typography>
        </Box>
      </Box>
      <Stack spacing={1}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={() => {
            void handleVacateShopper();
          }}
          disabled={actionLoading}
          size="medium"
        >
          {t("operations.debug.actions.vacate")}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          color="primary"
          onClick={() => {
            void handleAbandonShopper();
          }}
          disabled={actionLoading}
          size="medium"
        >
          {t("operations.debug.actions.abandon")}
        </Button>
      </Stack>
    </Box>
  );

  const renderInventoryList = () => (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {t("operations.debug.text.sampleItemsForStore")}
      </Typography>

      {loadingSampleInventory ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : sampleInventoryItems.length > 0 ? (
        <Stack spacing={1}>
          {sampleInventoryItems.map((item) => (
            <Box
              key={item.sku}
              onClick={() => setCurrentSku(item.sku)}
              sx={{
                p: 0,
                bgcolor: "common.white",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
                display: "flex",
                alignItems: "stretch",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "action.hover",
                },
              }}
            >
              {item.imageUrl && (
                <Box
                  component="img"
                  src={item.imageUrl}
                  alt={item.title || item.sku}
                  sx={{
                    width: 60,
                    height: 60,
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              )}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  p: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 0.5,
                    mb: 0.25,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    noWrap
                    sx={{
                      flex: 1,
                      fontWeight: 600,
                    }}
                  >
                    {item.title ||
                      t("operations.debug.labels.sku", {
                        sku: item.sku,
                      })}
                  </Typography>
                  <Box sx={{ display: "flex", flexShrink: 0, gap: 0.5 }}>
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        void navigator.clipboard.writeText(item.sku);
                        enqueueSnackbar(t("operations.debug.notifications.skuCopied"), {
                          variant: "success",
                        });
                      }}
                      sx={{
                        minWidth: "auto",
                        p: 0.5,
                        color: "text.secondary",
                        "&:hover": {
                          color: "primary.main",
                        },
                      }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </Button>
                    <Button
                      size="small"
                      component="a"
                      href={`${inventoryBaseUrl}/sample/${selectedStore?.id}/sku/${item.sku}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      sx={{
                        minWidth: "auto",
                        p: 0.5,
                        color: "text.secondary",
                        "&:hover": {
                          color: "primary.main",
                        },
                      }}
                    >
                      <LaunchIcon sx={{ fontSize: 14 }} />
                    </Button>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {t("operations.debug.labels.sku", {
                    sku: item.sku,
                  })}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      ) : (
        <Box sx={{ py: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {t("operations.debug.empty.noSampleInventory")}
          </Typography>
        </Box>
      )}

      {inventoryUrl && (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<OpenInNewIcon />}
          href={inventoryUrl}
          target="_blank"
          rel="noopener noreferrer"
          fullWidth
          sx={{ py: 1 }}
        >
          {t("operations.debug.actions.viewCompleteStoreInventory")}
        </Button>
      )}
    </Stack>
  );

  const renderRoomSessionHeader = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        mb: 2,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          color: "primary.main",
          fontSize: isMobile ? "1.1rem" : "1.15rem",
        }}
      >
        <AccountIcon /> {t("operations.debug.sections.roomSession")}
      </Typography>
      <Button
        variant="outlined"
        size="small"
        startIcon={<DevicesIcon />}
        component={RouterLink}
        to={roomDevicesUrl ?? "/devices"}
        disabled={!roomDevicesUrl}
        sx={{ flexShrink: 0 }}
      >
        {t("nav.devices")}
      </Button>
    </Box>
  );

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("layout.sidebar.craveAdminSection.fields.simulator")}
      icon={<SimulatorIcon />}
      titleAccessory={<LoadingRing loading={actionLoading} />}
    >
      <Stack spacing={2}>
        <Box>
          <AdminFilterPanel sx={{ mb: 0 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={isMobile ? 2 : 1.5}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <MuiAutocompleteMultiSelect
                  label={t("operations.debug.fields.selectStore")}
                  options={storeOptions}
                  value={selectedStore ? [selectedStore] : []}
                  onChange={(next) => handleStoreChange(null, next[0] ?? null)}
                  getOptionLabel={(option) => option?.label || ""}
                  getOptionKey={(option) => option.id}
                  placeholder={t("operations.debug.placeholders.selectStore")}
                  loading={loadingStores}
                />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <MuiAutocompleteMultiSelect
                  label={t("operations.debug.fields.selectChangingRoomGroup")}
                  options={groupOptions}
                  value={selectedGroup ? [selectedGroup] : []}
                  onChange={(next) => handleGroupChange(null, next[0] ?? null)}
                  getOptionLabel={(option) => option?.label || ""}
                  getOptionKey={(option) => option.id}
                  placeholder={t("operations.debug.placeholders.selectChangingRoomGroup")}
                  loading={loadingRoomGroups}
                  disabled={!selectedStore}
                />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <MuiAutocompleteMultiSelect
                  label={t("operations.debug.fields.selectChangingRoom")}
                  options={roomOptions}
                  value={selectedRoom ? [selectedRoom] : []}
                  onChange={(next) => handleRoomChange(null, next[0] ?? null)}
                  getOptionLabel={(option) => option?.label || ""}
                  getOptionKey={(option) => option.id}
                  placeholder={t("operations.debug.placeholders.selectChangingRoom")}
                  loading={loadingRooms}
                  disabled={!selectedGroup}
                />
              </Box>

              <Box sx={{ flexShrink: 0, alignSelf: { xs: "flex-start", md: "center" } }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="medium"
                  startIcon={<ClearIcon />}
                  disabled={!selectedStore && !selectedGroup && !selectedRoom}
                  onClick={() => handleStoreChange(null, null)}
                >
                  {t("common.clearFilters")}
                </Button>
              </Box>
            </Stack>
          </AdminFilterPanel>
        </Box>

        <AdminDrawer
          open={inventoryDrawerOpen}
          onClose={() => setInventoryDrawerOpen(false)}
          title={t("operations.debug.sections.inventory")}
          icon={<InventoryIcon />}
          closeLabel={t("operations.debug.actions.closeInventory")}
        >
          {renderInventoryList()}
        </AdminDrawer>

        {/* Room Session */}
        <Box>
          <Stack spacing={isMobile ? 2 : 2}>
            {shopper ? (
              // Active session
              <>
                <Paper sx={{ p: isMobile ? 2 : 2 }}>
                  {renderRoomSessionHeader()}

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                      {renderOverrideShopperControls()}
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                      {renderExitShopperControl()}
                    </Grid>
                  </Grid>
                </Paper>

                <Paper sx={{ p: isMobile ? 2 : 2 }}>
                  {/* Items Section */}
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          color: "primary.main",
                          fontSize: isMobile ? "1.1rem" : "1.15rem",
                        }}
                      >
                        <HangerIcon /> {t("operations.debug.sections.itemsAndRequests")}
                      </Typography>
                      {selectedRoom && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<InventoryIcon />}
                          onClick={() => setInventoryDrawerOpen(true)}
                        >
                          {t("operations.debug.sections.inventory")}
                        </Button>
                      )}
                    </Box>

                    {/* Action Sections */}
                    <Grid container spacing={2} alignItems="flex-start">
                      {/* Add Item Section */}
                      <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 1 } }}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 1,
                            bgcolor: "grey.50",
                            border: "1px solid",
                            borderColor: "divider",
                            height: "100%",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            gutterBottom
                            sx={{ mb: 1.5 }}
                          >
                            {t("operations.debug.actions.addItem")}
                          </Typography>
                          <Stack spacing={1}>
                            <TextField
                              size="small"
                              label={t("operations.debug.fields.sku")}
                              value={currentSku}
                              onChange={(e) => setCurrentSku(e.target.value)}
                              placeholder={t("operations.debug.placeholders.skuToAdd")}
                              fullWidth
                            />
                            <Button
                              fullWidth
                              variant="contained"
                              onClick={() => {
                                void handleAddSku();
                              }}
                              disabled={actionLoading || !currentSku}
                              size="medium"
                              sx={{ py: 0.75 }}
                            >
                              {t("operations.debug.actions.addItem")}
                            </Button>
                            <Button
                              fullWidth
                              variant="outlined"
                              onClick={() => {
                                void handleAddRandomItem();
                              }}
                              disabled={
                                actionLoading ||
                                loadingSampleInventory ||
                                allInventoryItems.length === 0
                              }
                              size="medium"
                              sx={{ py: 0.75 }}
                            >
                              {t("operations.debug.actions.addRandomItem")}
                            </Button>
                            <Divider />
                            <Button
                              fullWidth
                              variant="outlined"
                              onClick={() => {
                                void handleAddRandomOutOfStockItem();
                              }}
                              disabled={
                                actionLoading ||
                                loadingSampleInventory ||
                                allInventoryItems.length === 0
                              }
                              size="medium"
                              sx={{ py: 0.75 }}
                            >
                              {t("operations.debug.actions.addRandomOutOfStockItem")}
                            </Button>
                          </Stack>
                        </Box>
                      </Grid>

                      {/* Create Request Section */}
                      <Grid item xs={12} md={6} sx={{ order: { xs: 4, md: 2 } }}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 1,
                            bgcolor: "grey.50",
                            border: "1px solid",
                            borderColor: "divider",
                            height: "100%",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            gutterBottom
                            sx={{ mb: 1.5 }}
                          >
                            {t("operations.debug.actions.createRequest")}
                          </Typography>
                          <Stack spacing={1}>
                            <TextField
                              size="small"
                              label={t("operations.debug.fields.sku")}
                              value={requestSku}
                              onChange={(e) => setRequestSku(e.target.value)}
                              placeholder={t("operations.debug.placeholders.skuForRequest")}
                              fullWidth
                            />
                            <Button
                              fullWidth
                              variant="contained"
                              color="primary"
                              onClick={() => {
                                void handleCreateRequest();
                              }}
                              disabled={actionLoading || !requestSku}
                              size="medium"
                              sx={{ py: 0.75 }}
                            >
                              {t("operations.debug.actions.createRequest")}
                            </Button>
                            <Button
                              fullWidth
                              variant="outlined"
                              onClick={() => {
                                void handleCreateRandomRequest();
                              }}
                              disabled={
                                actionLoading ||
                                loadingSampleInventory ||
                                allInventoryItems.length === 0
                              }
                              size="medium"
                              sx={{ py: 0.75 }}
                            >
                              {t("operations.debug.actions.createRandomRequest")}
                            </Button>
                            <Divider sx={{ my: 0.5 }} />
                            <Button
                              fullWidth
                              variant="outlined"
                              onClick={() => {
                                void handleCreateAssociateRequest();
                              }}
                              disabled={actionLoading}
                              size="medium"
                              sx={{ py: 0.75 }}
                            >
                              {t("operations.debug.actions.createHelpRequest")}
                            </Button>
                          </Stack>
                        </Box>
                      </Grid>
                      {/* Requests Section */}
                      {requests.length > 0 && (
                        <Grid item xs={12} md={6} sx={{ order: { xs: 5, md: 4 } }}>
                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                mb: 1.5,
                              }}
                            >
                              <Typography variant="subtitle2" color="text.secondary">
                                {t("operations.debug.requestsCount", {
                                  count: requests.length,
                                })}
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() => {
                                  void handleCancelAllRequests();
                                }}
                                disabled={
                                  actionLoading ||
                                  requests.every((request) => !isRequestCancelable(request.status))
                                }
                              >
                                {t("operations.debug.actions.cancelAll")}
                              </Button>
                            </Box>
                            <Stack spacing={1}>
                              {requests.map((request) => (
                                <Paper
                                  key={request.id}
                                  variant="outlined"
                                  sx={{ p: isMobile ? 1.5 : 2 }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: isMobile ? "column" : "row",
                                      gap: isMobile ? 1.5 : 2,
                                    }}
                                  >
                                    {request.type === "ITEM" && request.enrichedItem?.imageUrl && (
                                      <Box
                                        component="img"
                                        src={request.enrichedItem.imageUrl}
                                        alt={request.enrichedItem.title || request.sku}
                                        sx={{
                                          width: isMobile ? 48 : 60,
                                          height: isMobile ? 48 : 60,
                                          objectFit: "cover",
                                          borderRadius: 1,
                                          alignSelf: isMobile ? "center" : "flex-start",
                                        }}
                                      />
                                    )}
                                    <Box sx={{ flex: 1 }}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "flex-start",
                                          justifyContent: "space-between",
                                          gap: 1,
                                          mb: 0.5,
                                        }}
                                      >
                                        <Typography variant="subtitle2" sx={{ flex: 1 }}>
                                          {request.type === "ITEM"
                                            ? request.enrichedItem?.title ||
                                              t("operations.debug.labels.sku", {
                                                sku: request.sku,
                                              })
                                            : request.type}
                                        </Typography>
                                        {request.type === "ITEM" && request.sku && (
                                          <Box sx={{ display: "flex", gap: 0.5 }}>
                                            <Button
                                              size="small"
                                              onClick={() => {
                                                navigator.clipboard.writeText(request.sku!);
                                                enqueueSnackbar(
                                                  t("operations.debug.notifications.skuCopied"),
                                                  {
                                                    variant: "success",
                                                  },
                                                );
                                              }}
                                              sx={{
                                                minWidth: "auto",
                                                p: 0.5,
                                                color: "text.secondary",
                                                "&:hover": {
                                                  color: "primary.main",
                                                },
                                              }}
                                            >
                                              <ContentCopyIcon sx={{ fontSize: 14 }} />
                                            </Button>
                                            <Button
                                              size="small"
                                              component="a"
                                              href={`${inventoryBaseUrl}/sample/${selectedStore?.id}/sku/${request.sku}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              sx={{
                                                minWidth: "auto",
                                                p: 0.5,
                                                color: "text.secondary",
                                                "&:hover": {
                                                  color: "primary.main",
                                                },
                                              }}
                                            >
                                              <LaunchIcon sx={{ fontSize: 14 }} />
                                            </Button>
                                          </Box>
                                        )}
                                      </Box>
                                      {request.type === "ITEM" && request.sku && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          display="block"
                                          align={isMobile ? "center" : "left"}
                                        >
                                          {t("operations.debug.labels.sku", {
                                            sku: request.sku,
                                          })}
                                        </Typography>
                                      )}
                                      {request.type === "ITEM" && request.enrichedItem && (
                                        <>
                                          {request.enrichedItem.brand && (
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              display="block"
                                              align={isMobile ? "center" : "left"}
                                            >
                                              {request.enrichedItem.brand}
                                            </Typography>
                                          )}
                                          {(request.enrichedItem.color ||
                                            request.enrichedItem.size) && (
                                            <Box
                                              sx={{
                                                display: "flex",
                                                gap: 2,
                                                mt: 0.5,
                                                justifyContent: isMobile ? "center" : "flex-start",
                                              }}
                                            >
                                              {request.enrichedItem.color && (
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  {t("operations.debug.labels.color", {
                                                    color: request.enrichedItem.color,
                                                  })}
                                                </Typography>
                                              )}
                                              {request.enrichedItem.size && (
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  {t("operations.debug.labels.size", {
                                                    size: request.enrichedItem.size,
                                                  })}
                                                </Typography>
                                              )}
                                            </Box>
                                          )}
                                          {(request.enrichedItem.price ||
                                            request.enrichedItem.salePrice) && (
                                            <Box
                                              sx={{
                                                display: "flex",
                                                gap: 1,
                                                mt: 0.5,
                                                alignItems: "center",
                                                justifyContent: isMobile ? "center" : "flex-start",
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                color="text.primary"
                                                fontWeight="medium"
                                              >
                                                {formatCurrencyInStore(
                                                  parseFloat(
                                                    (
                                                      request.enrichedItem.salePrice ||
                                                      request.enrichedItem.price ||
                                                      "0"
                                                    ).replace(/[^0-9.]/g, ""),
                                                  ),
                                                  selectedStore
                                                    ? stores.find((s) => s.id === selectedStore.id)
                                                    : null,
                                                )}
                                              </Typography>
                                              {request.enrichedItem.salePrice &&
                                                request.enrichedItem.price && (
                                                  <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                      textDecoration: "line-through",
                                                    }}
                                                  >
                                                    {formatCurrencyInStore(
                                                      parseFloat(
                                                        request.enrichedItem.price.replace(
                                                          /[^0-9.]/g,
                                                          "",
                                                        ),
                                                      ),
                                                      selectedStore
                                                        ? stores.find(
                                                            (s) => s.id === selectedStore.id,
                                                          )
                                                        : null,
                                                    )}
                                                  </Typography>
                                                )}
                                            </Box>
                                          )}
                                        </>
                                      )}
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: 2,
                                          mt: 1,
                                          flexWrap: "wrap",
                                          justifyContent: isMobile ? "center" : "flex-start",
                                        }}
                                      >
                                        <Typography variant="caption" color="text.secondary">
                                          {t("operations.debug.labels.created", {
                                            time: formatStoreTimeWithTimezone(
                                              request.createdAt,
                                              effectiveTimezone,
                                              i18n.resolvedLanguage || i18n.language || "en-US",
                                            ),
                                          })}
                                        </Typography>
                                        {request.assignedAt && (
                                          <Typography variant="caption" color="text.secondary">
                                            {t("operations.debug.labels.assigned", {
                                              time: formatStoreTimeWithTimezone(
                                                request.assignedAt,
                                                effectiveTimezone,
                                                i18n.resolvedLanguage || i18n.language || "en-US",
                                              ),
                                            })}
                                          </Typography>
                                        )}
                                        {request.completedAt && (
                                          <Typography variant="caption" color="text.secondary">
                                            {t("operations.debug.labels.completed", {
                                              time: formatStoreTimeWithTimezone(
                                                request.completedAt,
                                                effectiveTimezone,
                                                i18n.resolvedLanguage || i18n.language || "en-US",
                                              ),
                                            })}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  </Box>
                                </Paper>
                              ))}
                            </Stack>
                          </Box>
                        </Grid>
                      )}

                      {/* Existing Items List */}
                      {items.length > 0 && (
                        <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 3 } }}>
                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 1.5,
                              }}
                            >
                              <Typography variant="subtitle2" color="text.secondary">
                                {t("operations.debug.itemsCount", {
                                  count: items.length,
                                })}
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() => {
                                  void handleClearAllItems();
                                }}
                                disabled={actionLoading}
                              >
                                {t("operations.debug.actions.clearAll")}
                              </Button>
                            </Box>
                            <Stack spacing={1}>
                              {items.map((item) => (
                                <Paper
                                  key={item.sku}
                                  variant="outlined"
                                  sx={{ p: isMobile ? 1.5 : 2 }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: isMobile ? "column" : "row",
                                      gap: isMobile ? 1.5 : 2,
                                    }}
                                  >
                                    {item.imageUrl && (
                                      <Box
                                        component="img"
                                        src={item.imageUrl}
                                        alt={item.title || item.sku}
                                        sx={{
                                          width: isMobile ? 48 : 60,
                                          height: isMobile ? 48 : 60,
                                          objectFit: "cover",
                                          borderRadius: 1,
                                          alignSelf: isMobile ? "center" : "flex-start",
                                        }}
                                      />
                                    )}
                                    <Box sx={{ flex: 1 }}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "flex-start",
                                          justifyContent: "space-between",
                                          gap: 1,
                                          mb: 0.5,
                                        }}
                                      >
                                        <Typography variant="subtitle2" sx={{ flex: 1 }}>
                                          {item.title ||
                                            t("operations.debug.labels.sku", {
                                              sku: item.sku,
                                            })}
                                        </Typography>
                                        <Box sx={{ display: "flex", gap: 0.5 }}>
                                          <Button
                                            size="small"
                                            onClick={() => {
                                              navigator.clipboard.writeText(item.sku);
                                              enqueueSnackbar(
                                                t("operations.debug.notifications.skuCopied"),
                                                {
                                                  variant: "success",
                                                },
                                              );
                                            }}
                                            sx={{
                                              minWidth: "auto",
                                              p: 0.5,
                                              color: "text.secondary",
                                              "&:hover": {
                                                color: "primary.main",
                                              },
                                            }}
                                          >
                                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                                          </Button>
                                          <Button
                                            size="small"
                                            component="a"
                                            href={`${inventoryBaseUrl}/sample/${selectedStore?.id}/sku/${item.sku}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                              minWidth: "auto",
                                              p: 0.5,
                                              color: "text.secondary",
                                              "&:hover": {
                                                color: "primary.main",
                                              },
                                            }}
                                          >
                                            <LaunchIcon sx={{ fontSize: 14 }} />
                                          </Button>
                                        </Box>
                                      </Box>

                                      {item.title && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          display="block"
                                          align={isMobile ? "center" : "left"}
                                        >
                                          {t("operations.debug.labels.sku", {
                                            sku: item.sku,
                                          })}
                                        </Typography>
                                      )}

                                      {item.brand && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          display="block"
                                          align={isMobile ? "center" : "left"}
                                        >
                                          {item.brand}
                                        </Typography>
                                      )}
                                      {(item.color || item.size) && (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            gap: 2,
                                            mt: 0.5,
                                            justifyContent: isMobile ? "center" : "flex-start",
                                          }}
                                        >
                                          {item.color && (
                                            <Typography variant="caption" color="text.secondary">
                                              {t("operations.debug.labels.color", {
                                                color: item.color,
                                              })}
                                            </Typography>
                                          )}
                                          {item.size && (
                                            <Typography variant="caption" color="text.secondary">
                                              {t("operations.debug.labels.size", {
                                                size: item.size,
                                              })}
                                            </Typography>
                                          )}
                                        </Box>
                                      )}
                                      {(item.price || item.salePrice) && (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            gap: 1,
                                            mt: 0.5,
                                            alignItems: "center",
                                            justifyContent: isMobile ? "center" : "flex-start",
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            color="text.primary"
                                            fontWeight="medium"
                                          >
                                            {formatCurrencyInStore(
                                              parseFloat(
                                                (item.salePrice || item.price || "0").replace(
                                                  /[^0-9.]/g,
                                                  "",
                                                ),
                                              ),
                                              selectedStore
                                                ? stores.find((s) => s.id === selectedStore.id)
                                                : null,
                                            )}
                                          </Typography>
                                          {item.salePrice && item.price && (
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ textDecoration: "line-through" }}
                                            >
                                              {formatCurrencyInStore(
                                                parseFloat(item.price.replace(/[^0-9.]/g, "")),
                                                selectedStore
                                                  ? stores.find((s) => s.id === selectedStore.id)
                                                  : null,
                                              )}
                                            </Typography>
                                          )}
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                </Paper>
                              ))}
                            </Stack>
                          </Box>
                        </Grid>
                      )}
                      {items.length > 0 && requests.length > 0 && (
                        <Grid item xs={12} sx={{ display: { xs: "block", md: "none" }, order: 3 }}>
                          <Divider sx={{ my: 2.5, borderColor: "divider" }} />
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Paper>
              </>
            ) : (
              <>
                {selectedStore && selectedGroup && selectedRoom && hasLoadedRoomState && (
                  <>
                    <Paper sx={{ p: isMobile ? 2 : 2 }}>
                      {renderRoomSessionHeader()}
                      <Box>{renderCreateShopperControls()}</Box>
                    </Paper>

                    <Paper sx={{ p: isMobile ? 2 : 2 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          color: "primary.main",
                          fontSize: isMobile ? "1.1rem" : "1.15rem",
                          mb: 2,
                        }}
                      >
                        <HangerIcon /> {t("operations.debug.sections.itemsAndRequests")}
                      </Typography>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 1,
                          bgcolor: "grey.50",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          gutterBottom
                          sx={{ mb: 1.5 }}
                        >
                          {t("operations.debug.actions.createRequest")}
                        </Typography>
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={() => {
                            void handleCreateAssociateRequest();
                          }}
                          disabled={actionLoading}
                          size="medium"
                          sx={{ py: 0.75 }}
                        >
                          {t("operations.debug.actions.createHelpRequest")}
                        </Button>
                      </Box>
                      {requests.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                              mb: 1.5,
                            }}
                          >
                            <Typography variant="subtitle2" color="text.secondary">
                              {t("operations.debug.requestsCount", { count: requests.length })}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => {
                                void handleCancelAllRequests();
                              }}
                              disabled={
                                actionLoading ||
                                requests.every((request) => !isRequestCancelable(request.status))
                              }
                            >
                              {t("operations.debug.actions.cancelAll")}
                            </Button>
                          </Box>
                          <Stack spacing={1}>
                            {requests.map((request) => (
                              <Paper
                                key={request.id}
                                variant="outlined"
                                sx={{ p: isMobile ? 1.5 : 2 }}
                              >
                                <Typography variant="subtitle2">
                                  {request.type === "ASSOCIATE"
                                    ? t("liveView.fittingRooms.shopperDetails.requests.type.help")
                                    : request.type}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  {t("operations.debug.labels.created", {
                                    time: formatStoreTimeWithTimezone(
                                      request.createdAt,
                                      effectiveTimezone,
                                      i18n.resolvedLanguage || i18n.language || "en-US",
                                    ),
                                  })}
                                </Typography>
                              </Paper>
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Paper>
                  </>
                )}
              </>
            )}
          </Stack>
        </Box>
      </Stack>
    </PageShell>
  );
};

export default Simulator;
